import {
  createShopApiGraphqlClient,
} from "../../../../packages/pvc-product/shop-api-transport";

export type BusinessContactSettings = {
  businessName: string;
  phone: string | null;
  whatsappNumber: string | null;
  whatsappUrl: string | null;
  email: string | null;
  addressText: string | null;
  openingHoursText: string | null;
};

/**
 * Owner-verified public details. These are a truthful launch fallback only:
 * the Site Settings Shop API remains authoritative whenever it is available.
 */
export const VERIFIED_BUSINESS_SETTINGS: BusinessContactSettings = {
  businessName: "בית דפוס רק בשמחות",
  phone: "0524149149",
  whatsappNumber: "+972524149149",
  whatsappUrl: "https://wa.me/972524149149",
  email: "info@happyprint.co.il",
  addressText: "חרושת 30, קריית ביאליק, 2751047, ישראל",
  openingHoursText: "א׳–ה׳ 08:30–18:00\nו׳ 08:30–14:00\nשבת סגור",
};

const BUSINESS_SETTINGS_QUERY = `
  query HappyPrintBusinessContactSettings {
    happyPrintPublicSiteSettings {
      businessName
      phone
      whatsappNumber
      whatsappUrl
      email
      addressText
      openingHoursText
    }
  }
`;

function record(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function nullableString(value: unknown, path: string): string | null {
  if (value == null) return null;
  if (typeof value !== "string") throw new Error(`${path} must be a string or null`);
  return value;
}

function decodeSettings(value: unknown): BusinessContactSettings | null {
  if (value == null) return null;
  const item = record(value, "data.happyPrintPublicSiteSettings");
  const businessName = item.businessName;
  if (typeof businessName !== "string" || !businessName.trim()) {
    throw new Error("data.happyPrintPublicSiteSettings.businessName must be a non-empty string");
  }
  return {
    businessName,
    phone: nullableString(item.phone, "data.happyPrintPublicSiteSettings.phone"),
    whatsappNumber: nullableString(item.whatsappNumber, "data.happyPrintPublicSiteSettings.whatsappNumber"),
    whatsappUrl: nullableString(item.whatsappUrl, "data.happyPrintPublicSiteSettings.whatsappUrl"),
    email: nullableString(item.email, "data.happyPrintPublicSiteSettings.email"),
    addressText: nullableString(item.addressText, "data.happyPrintPublicSiteSettings.addressText"),
    openingHoursText: nullableString(item.openingHoursText, "data.happyPrintPublicSiteSettings.openingHoursText"),
  };
}

export async function loadBusinessContactSettings(signal?: AbortSignal): Promise<BusinessContactSettings> {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  const client = createShopApiGraphqlClient({ endpoint });
  try {
    const raw = await client.execute({
      operationName: "HappyPrintBusinessContactSettings",
      document: BUSINESS_SETTINGS_QUERY,
      signal,
    });
    const data = record(raw, "data");
    return decodeSettings(data.happyPrintPublicSiteSettings) ?? VERIFIED_BUSINESS_SETTINGS;
  } catch (error) {
    if (signal?.aborted) throw error;
    return VERIFIED_BUSINESS_SETTINGS;
  }
}

export function formatPhoneForDisplay(phone: string | null): string | null {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, "");
  if (/^05\d{8}$/.test(digits)) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

export function buildBusinessContactLinks(
  settings: BusinessContactSettings,
  whatsappMessage?: string,
): { phoneHref: string | null; whatsappHref: string | null; emailHref: string | null } {
  const phone = settings.phone?.replace(/[^0-9+]/g, "") || null;
  const whatsappDigits = settings.whatsappNumber?.replace(/\D/g, "") || null;
  const whatsappBase = whatsappDigits ? `https://wa.me/${whatsappDigits}` : settings.whatsappUrl;
  const whatsappHref = whatsappBase
    ? whatsappMessage
      ? `${whatsappBase}${whatsappBase.includes("?") ? "&" : "?"}text=${encodeURIComponent(whatsappMessage)}`
      : whatsappBase
    : null;

  return {
    phoneHref: phone ? `tel:${phone}` : null,
    whatsappHref,
    emailHref: settings.email ? `mailto:${settings.email}` : null,
  };
}
